import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

// Axios default konfiguratsiya
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Axios interceptor - 401 xatolarni ushlash (faqat asosiy API uchun)
let isRedirecting = false;
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const requestUrl = error.config?.url || '';
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const isChatServer = requestUrl.includes('chat.flance.uz') || requestUrl.includes('/api/chat/');
        const isOwnApi = requestUrl.startsWith('/') || requestUrl.startsWith(baseUrl);

        if (error.response?.status === 401 && !isRedirecting && isOwnApi && !isChatServer) {
            isRedirecting = true;
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setTimeout(() => {
                useAuthStore.getState().logout();
                window.location.href = '/authpage';
            }, 0);
        }
        return Promise.reject(error);
    }
);

// Axios request interceptor - token qo'shish
axios.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().token || localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            loading: false,
            error: null,

            // Login
            login: async (values) => {
                set({ loading: true, error: null });
                try {
                    const response = await axios.post('/auth/login', values);
                    const { user, token } = response.data;
                    
                    set({ user, token, loading: false });
                    
                    // Eski localStorage uchun (backward compatibility)
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('token', token);
                    
                    return { success: true, user };
                } catch (error) {
                    set({ 
                        loading: false, 
                        error: error.response?.data?.message || "Ma'lumotlardan biri noto'g'ri" 
                    });
                    return { success: false, error: error.response?.data?.message };
                }
            },

            // Register
            register: async (values) => {
                set({ loading: true, error: null });
                try {
                    const response = await axios.post('/auth/register', values);
                    const { user, token } = response.data;
                    
                    set({ user, token, loading: false });
                    
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('token', token);
                    
                    return { success: true, user };
                } catch (error) {
                    set({ 
                        loading: false, 
                        error: error.response?.data?.message || "Ro'yxatdan o'tishda xatolik" 
                    });
                    return { success: false, error: error.response?.data?.message };
                }
            },

            // Logout
            logout: () => {
                localStorage.removeItem('user');
                localStorage.removeItem('token');
                setTimeout(() => {
                    set({ user: null, token: null, loading: false, error: null });
                }, 0);
            },

            // Check auth (sahifa yuklanganda)
            checkAuth: () => {
                const storedUser = localStorage.getItem('user');
                const storedToken = localStorage.getItem('token') || localStorage.getItem('access_token');
                
                if (storedUser && storedToken) {
                    try {
                        const user = JSON.parse(storedUser);
                        set({ user, token: storedToken });
                        return true;
                    } catch {
                        get().logout();
                        return false;
                    }
                }
                return false;
            },

            // User rolini tekshirish
            hasRole: (role) => {
                const user = get().user;
                if (!user) return false;
                
                if (typeof user.role === 'string') return user.role === role;
                if (typeof user.role_slug === 'string') return user.role_slug === role;
                if (Array.isArray(user.roles)) {
                    return user.roles.some(r => 
                        (typeof r === 'string' && r === role) || 
                        (r?.slug === role)
                    );
                }
                return false;
            },

            // Authenticated tekshirish
            isAuthenticated: () => {
                const { user, token } = get();
                return !!(user && token);
            },

            // Clear error
            clearError: () => set({ error: null }),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({ user: state.user, token: state.token }),
            merge: (persisted, current) => {
                const merged = { ...current, ...persisted };
                // Eski login oqimi access_token yoki token key da saqlaydi
                if (!merged.token) {
                    merged.token = localStorage.getItem('token') || localStorage.getItem('access_token') || null;
                }
                if (!merged.user) {
                    try { merged.user = JSON.parse(localStorage.getItem('user')); } catch { /* noop */ }
                }
                return merged;
            },
        }
    )
);
